import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { ShieldCheck, User } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const ProfileCompletionModal = ({ open, onClose, user, token, API, onProfileComplete }) => {
  const [fullName, setFullName] = useState(user?.name || "");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFullName(user.name || "");
    }
  }, [user]);

  const handleSubmit = async () => {
    if (!fullName.trim()) {
      toast.error("Por favor ingresa tu nombre completo");
      return;
    }

    if (!privacyAccepted) {
      toast.error("Debes aceptar los términos de privacidad");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/complete-profile`,
        {
          full_name: fullName.trim(),
          whatsapp: "",
          referral_source: "direct"
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      toast.success("¡Perfil completado!");
      onProfileComplete?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar perfil");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-[#475569]" />
            Completa tu Perfil
          </DialogTitle>
          <DialogDescription>
            Para participar en compras y subastas, necesitamos tu nombre completo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Account Info */}
          {user?.email && (
            <div className="p-3 bg-[#F1F5F9] rounded-xl">
              <p className="text-xs text-[#64748B] flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#10B981]" />
                Conectado como: <span className="font-medium text-[#0F172A]">{user.email}</span>
              </p>
            </div>
          )}

          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="fullName" className="flex items-center gap-2">
              <User className="h-4 w-4 text-[#64748B]" />
              Nombre Completo
            </Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Ej: Juan Pérez"
              className="rounded-xl"
              data-testid="profile-full-name"
            />
          </div>

          {/* Privacy Notice */}
          <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <Checkbox
              id="privacy-accepted"
              checked={privacyAccepted}
              onCheckedChange={setPrivacyAccepted}
              data-testid="profile-privacy-checkbox"
            />
            <label
              htmlFor="privacy-accepted"
              className="text-sm text-blue-800 cursor-pointer leading-tight"
            >
              Acepto los <strong>términos y condiciones</strong> de Clueer. Entiendo que mis datos serán privados y usados únicamente para gestionar mis compras y subastas.
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={handleSubmit}
            disabled={loading || !fullName || !privacyAccepted}
            className="w-full rounded-full h-12 bg-[#475569] hover:bg-[#334155]"
            data-testid="profile-submit"
          >
            {loading ? (
              <div className="spinner" style={{ width: 20, height: 20 }} />
            ) : (
              "Guardar y Continuar"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileCompletionModal;
